/**
 * Top-down minimap (Minecraft map style, north locked).
 * Samples the topmost solid block color around the player on a
 * throttled refresh; draws the player as a rotating arrow and
 * remote players as white dots.
 */

import { BlockType, CHUNK_SIZE, getBlockIndex } from "@craftgaming/shared";
import { getBlockColor } from "./hotbar";

const VIEW_RADIUS = 32; // blocks each side of the player
const PIXELS_PER_BLOCK = 2;
const CANVAS_SIZE = VIEW_RADIUS * 2 * PIXELS_PER_BLOCK; // 128
const REFRESH_INTERVAL = 0.4; // seconds
const SCAN_UP = 12; // blocks above the player to start scanning
const SCAN_DEPTH = 48; // how far down to look for ground

export type MinimapMarker = { x: number; z: number };

export type Minimap = {
  update: (
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    yaw: number,
    markers: MinimapMarker[]
  ) => void;
  dispose: () => void;
};

export type MinimapWorldAccess = {
  /** Direct chunk buffer access — avoids per-block key allocation. */
  getChunkBlocks: (cx: number, cy: number, cz: number) => Uint16Array | undefined;
};

export function createMinimap(world: MinimapWorldAccess): Minimap {
  const container = document.createElement("div");
  container.id = "minimap";
  container.style.cssText = `
    position: absolute;
    top: 12px;
    right: 12px;
    width: ${CANVAS_SIZE}px;
    height: ${CANVAS_SIZE}px;
    border: 3px solid rgba(0, 0, 0, 0.6);
    border-radius: 8px;
    overflow: hidden;
    pointer-events: none;
    box-shadow: 0 2px 10px rgba(0,0,0,0.4);
  `;

  const terrainCanvas = document.createElement("canvas");
  terrainCanvas.width = CANVAS_SIZE;
  terrainCanvas.height = CANVAS_SIZE;
  terrainCanvas.style.cssText = "position:absolute;inset:0;image-rendering:pixelated;";
  container.appendChild(terrainCanvas);

  const markerCanvas = document.createElement("canvas");
  markerCanvas.width = CANVAS_SIZE;
  markerCanvas.height = CANVAS_SIZE;
  markerCanvas.style.cssText = "position:absolute;inset:0;";
  container.appendChild(markerCanvas);

  const overlay = document.getElementById("ui-overlay");
  (overlay ?? document.body).appendChild(container);

  const terrainCtx = terrainCanvas.getContext("2d");
  const markerCtx = markerCanvas.getContext("2d");

  let refreshTimer = REFRESH_INTERVAL; // draw on first frame

  function topBlockColor(wx: number, wz: number, playerY: number): string | null {
    const startY = Math.floor(playerY) + SCAN_UP;
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;

    // One chunk lookup per 16 vertical blocks instead of one per block
    let cachedCy = Number.NaN;
    let blocks: Uint16Array | undefined;

    for (let y = startY; y > startY - SCAN_DEPTH && y >= 0; y--) {
      const cy = Math.floor(y / CHUNK_SIZE);
      if (cy !== cachedCy) {
        cachedCy = cy;
        blocks = world.getChunkBlocks(cx, cy, cz);
        if (!blocks) {
          // Unloaded chunk — skip its whole vertical span at once
          y = cy * CHUNK_SIZE;
          continue;
        }
      }
      if (!blocks) continue;

      const ly = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
      const block = blocks[getBlockIndex(lx, ly, lz)];
      if (block !== BlockType.AIR) {
        if (block === BlockType.WATER) return "#3b6fd4";
        const color = getBlockColor(block);
        // Shade by depth relative to the player for a hint of relief
        const depth = Math.max(-8, Math.min(8, y - Math.floor(playerY)));
        return shadeColor(color, 1 + depth * 0.03);
      }
    }
    return null;
  }

  function shadeColor(hex: string, factor: number): string {
    const n = parseInt(hex.slice(1), 16);
    const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 0xff) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 0xff) * factor)));
    const b = Math.max(0, Math.min(255, Math.round((n & 0xff) * factor)));
    return `rgb(${r},${g},${b})`;
  }

  function drawTerrain(playerX: number, playerY: number, playerZ: number) {
    if (!terrainCtx) return;
    const baseX = Math.floor(playerX) - VIEW_RADIUS;
    const baseZ = Math.floor(playerZ) - VIEW_RADIUS;

    terrainCtx.fillStyle = "#111";
    terrainCtx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    for (let dz = 0; dz < VIEW_RADIUS * 2; dz++) {
      for (let dx = 0; dx < VIEW_RADIUS * 2; dx++) {
        const color = topBlockColor(baseX + dx, baseZ + dz, playerY);
        if (!color) continue;
        terrainCtx.fillStyle = color;
        terrainCtx.fillRect(dx * PIXELS_PER_BLOCK, dz * PIXELS_PER_BLOCK, PIXELS_PER_BLOCK, PIXELS_PER_BLOCK);
      }
    }
  }

  function drawMarkers(playerX: number, playerZ: number, yaw: number, markers: MinimapMarker[]) {
    if (!markerCtx) return;
    markerCtx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const center = CANVAS_SIZE / 2;

    // Remote players — white dots
    markerCtx.fillStyle = "#ffffff";
    for (const marker of markers) {
      const mx = center + (marker.x - playerX) * PIXELS_PER_BLOCK;
      const mz = center + (marker.z - playerZ) * PIXELS_PER_BLOCK;
      if (mx < 2 || mx > CANVAS_SIZE - 2 || mz < 2 || mz > CANVAS_SIZE - 2) continue;
      markerCtx.beginPath();
      markerCtx.arc(mx, mz, 3, 0, Math.PI * 2);
      markerCtx.fill();
      markerCtx.strokeStyle = "#000";
      markerCtx.lineWidth = 1;
      markerCtx.stroke();
    }

    // Local player — arrow rotated by yaw (screen -z = world -z = north)
    markerCtx.save();
    markerCtx.translate(center, center);
    markerCtx.rotate(-yaw);
    markerCtx.fillStyle = "#ffdd44";
    markerCtx.strokeStyle = "#000";
    markerCtx.lineWidth = 1.5;
    markerCtx.beginPath();
    markerCtx.moveTo(0, -6);
    markerCtx.lineTo(4.5, 5);
    markerCtx.lineTo(0, 2.5);
    markerCtx.lineTo(-4.5, 5);
    markerCtx.closePath();
    markerCtx.fill();
    markerCtx.stroke();
    markerCtx.restore();

    // North indicator
    markerCtx.fillStyle = "rgba(255,255,255,0.85)";
    markerCtx.font = "bold 10px monospace";
    markerCtx.textAlign = "center";
    markerCtx.fillText("N", center, 11);
  }

  function update(
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    yaw: number,
    markers: MinimapMarker[]
  ) {
    refreshTimer += dt;
    if (refreshTimer >= REFRESH_INTERVAL) {
      refreshTimer = 0;
      drawTerrain(playerX, playerY, playerZ);
    }
    drawMarkers(playerX, playerZ, yaw, markers);
  }

  return {
    update,
    dispose() {
      container.remove();
    },
  };
}
